import { instance } from "utils/axios";

// ---------------------------------------------------------------------------
// HOLISTIC (College-Prep) REVIEW — frontend service.
// Talks to the real tuition-api student endpoints:
//   GET  /student/holistic-review   -> my portfolio (application_review + children)
//   POST /student/holistic-review   -> save my portfolio (replace children)
//   GET  /student/holistic-report   -> portfolio + current SUBMITTED advisor version
// The backend returns the Sequelize shape (vision_items, target_universities,
// review_scores ...). This file is the ADAPTER: it maps that to the flat shape
// the College-Prep pages consume (vision, universities, reviewScores ...), so the
// pages need no changes.
// ---------------------------------------------------------------------------

// option_list lookup. Stored value = string `code`; admin-managed in tuition-api,
// but kept here (mirrors the seed) so the form dropdowns work without an extra call.
export const OPTION_LISTS = {
  EXAM_TYPE: [
    { code: "IELTS", label: "IELTS" },
    { code: "TOEFL", label: "TOEFL" },
    { code: "SAT", label: "SAT" },
    { code: "ACT", label: "ACT" },
    { code: "AP", label: "AP" },
    { code: "DUOLINGO", label: "Duolingo" },
  ],
  ACTIVITY_TYPE: [
    "Academic", "Art", "Athletics: Club", "Athletics: JV/Varsity",
    "Career oriented", "Community/Service/Volunteer", "Computer/Technology",
    "Cultural", "Dance", "Debate/Speech", "Environmental",
    "Family responsibilities", "Foreign exchange", "Internship", "Journalism",
    "Publication", "Junior R.O.T.C", "LGBT", "Music instrumental",
    "Music: Vocal", "Religious", "Robotics", "School Spirit", "Science/Math",
    "Social Justice", "Student Govt/Politics", "Theater/drama", "Work (Paid)",
    "Other club/Activity",
  ].map((label) => ({ code: label, label })),
  RECOGNITION_LEVEL: [
    { code: "SCHOOL", label: "School" },
    { code: "STATE_REGIONAL", label: "State/Regional" },
    { code: "NATIONAL", label: "National" },
    { code: "INTERNATIONAL", label: "International" },
  ],
  GRADE_LEVEL: [
    { code: "G9", label: "9" },
    { code: "G10", label: "10" },
    { code: "G11", label: "11" },
    { code: "G12", label: "12" },
  ],
  EC_TIMING: [
    { code: "ALL_YEAR", label: "all-year" },
    { code: "SCHOOL_YEAR", label: "school-year" },
    { code: "SCHOOL_BREAK", label: "school-break" },
  ],
};

// 7 radar axes. `section` ties each axis to the student form section it scores.
export const REVIEW_DIMENSIONS = [
  { code: "SCHOOL_RESEARCH", label: "Сургууль судалгаа", section: "universities" },
  { code: "EXTRACURRICULAR", label: "ХГҮА", section: "extracurriculars" },
  { code: "HONORS", label: "Шагнал, амжилт", section: "honors" },
  { code: "REC_LETTERS", label: "Тодорхойлох захидал", section: "recLetters" },
  { code: "VISION", label: "Алсын хараа", section: "vision" },
  { code: "ASPIRATION", label: "Хүсэл эрмэлзэл", section: "passion" },
  { code: "ACADEMIC_EXAM", label: "Академик шалгалт", section: "exams" },
];

export const optionLabel = (category, code) =>
  (OPTION_LISTS[category] || []).find((o) => o.code === code)?.label || code;

export const dimensionLabel = (code) =>
  REVIEW_DIMENSIONS.find((d) => d.code === code)?.label || code;

export const getOptionList = (category) =>
  Promise.resolve(OPTION_LISTS[category] || []);

export const emptyReview = () => ({
  cycle: "2027",
  status: "DRAFT",
  bestSubject: "",
  teacherStudentRelation: "",
  vision: [],
  passion: [],
  recLetters: [],
  honors: [],
  extracurriculars: [],
  universities: [],
  exams: [],
});

// ---- mappers: backend (Sequelize) -> flat shape the pages consume ----

const gradeCodes = (arr) => (arr || []).map((g) => g.gradeLevel);

const mapStudent = (r) => {
  if (!r) return null;
  return {
    cycle: r.cycle || "2027",
    status: r.status || "DRAFT",
    bestSubject: r.bestSubject || "",
    teacherStudentRelation: r.teacherStudentRelation || "",
    vision: (r.vision_items || []).map((v) => ({
      socialIssue: v.socialIssue, rootCause: v.rootCause, contribution: v.contribution,
    })),
    passion: (r.passion_items || []).map((v) => ({
      profession: v.profession, motivation: v.motivation, experience: v.experience,
    })),
    recLetters: (r.recommendation_letters || []).map((v) => ({
      teacher: v.teacher, memories: v.memories,
    })),
    honors: (r.honors || []).map((v) => ({
      recognitionLevel: v.recognitionLevel, description: v.description,
      gradeLevels: gradeCodes(v.grade_levels),
    })),
    extracurriculars: (r.extracurriculars || []).map((v) => ({
      activityType: v.activityType, position: v.position, organization: v.organization,
      description: v.description, timing: v.timing,
      hoursPerWeek: v.hoursPerWeek, weeksPerYear: v.weeksPerYear,
      gradeLevels: gradeCodes(v.grade_levels),
    })),
    universities: (r.target_universities || []).map((v) => ({
      country: v.country, name: v.name, reasons: v.reasons,
    })),
    exams: (r.exam_results || []).map((v) => ({
      examType: v.examType, taken: v.taken, officialScore: v.officialScore, mockScore: v.mockScore,
    })),
  };
};

const advisorName = (a) =>
  a ? `${a.lastName || ""} ${a.firstName || ""}`.trim() : "";

// hiddenItems is stored as a JSON string {"<section>":[index,...]} on the version.
const parseHiddenItems = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    const o = JSON.parse(raw);
    return o && typeof o === "object" ? o : {};
  } catch {
    return {};
  }
};

const mapVersion = (v) => {
  if (!v) return null;
  const reviewScores = (v.review_scores || []).map((s) => ({
    dimension: s.dimension, score: s.score,
    rubricDescription: s.rubricDescription, advisorNote: s.advisorNote,
  }));
  // overall % = average over ALL dimensions (missing dimension counts as 0)
  const overallScore = REVIEW_DIMENSIONS.length
    ? Math.round(
        REVIEW_DIMENSIONS.reduce((s, d) => {
          const found = reviewScores.find((r) => r.dimension === d.code);
          return s + (found?.score || 0);
        }, 0) / REVIEW_DIMENSIONS.length
      )
    : 0;
  return {
    versionNo: v.versionNo,
    advisor: advisorName(v.advisor),
    reviewedAt: v.reviewedAt ? String(v.reviewedAt).slice(0, 10) : "",
    isCurrent: !!v.isCurrent,
    reviewScores,
    overallScore,
    overallReadiness: v.overallReadiness || "",
    progressSummary: v.progressSummary || "",
    counselorMessage: v.counselorMessage || "",
    counselorPhoto: v.counselorPhoto || "",
    todo: (v.todo_recommendations || []).map((t) => ({
      advice: t.advice, importance: t.importance,
    })),
    // Story Building / Өргөдлийн төлөвлөгөө
    themeFuture: v.themeFuture || "",
    themeReason: v.themeReason || "",
    themeStories: v.themeStories || "",
    storyWhy: v.storyWhy || "",
    storyWhat: v.storyWhat || "",
    storyHow: v.storyHow || "",
    storyCan: v.storyCan || "",
    // per-section publish flags (student sees a section only when its flag is true)
    storyPublished: !!v.storyPublished,
    todoPublished: !!v.todoPublished,
    // when true, the whole student-filled portfolio is hidden from parents
    hideStudentData: !!v.hideStudentData,
    // per-item parent-hide map: { <section>: [index, ...] }
    hiddenItems: parseHiddenItems(v.hiddenItems),
    status: v.status,
  };
};

// ---- Student portfolio (application_review + children) ----

export const getMyReview = () =>
  instance({ method: "get", url: "/student/holistic-review" })
    .then((res) => mapStudent(res.data) || emptyReview())
    .catch(() => emptyReview());

// backend POST body already matches the flat page shape (vision, honors+gradeLevels[]...)
export const saveMyReview = (data, status) =>
  instance({
    method: "post",
    url: "/student/holistic-review",
    data: { ...data, status: status || data.status || "DRAFT" },
  }).then((res) => mapStudent(res.data));

// report endpoint: a student sees their own; a parent passes a child studentId.
const reportUrl = (studentId) =>
  studentId ? `/guardian/student/${studentId}/holistic-report` : "/student/holistic-report";

// ---- Report = portfolio + current SUBMITTED advisor version (chart gate) ----
// `reviewed:false` lets a caller force the "not yet reviewed" state for preview.
// `studentId` (optional) = a parent viewing a specific child.
export const getReport = ({ reviewed, versionNo, studentId } = {}) =>
  instance({ method: "get", url: reportUrl(studentId) }).then((res) => {
    const d = res.data || {};
    const student = mapStudent(d.student) || emptyReview();
    const version = mapVersion(d.version);
    const isReviewed = reviewed === false ? false : !!d.reviewed;
    return {
      cycle: d.cycle || student.cycle || "2027",
      reviewed: isReviewed,
      student,
      // version returned even when the radar isn't "reviewed" — story/To-Do tabs
      // gate independently on version.storyPublished / version.todoPublished.
      version,
    };
  });

// History list for the selector. The API exposes only the current submitted
// version, so this returns at most one entry (newest).
export const getReviewVersions = (studentId) =>
  instance({ method: "get", url: reportUrl(studentId) })
    .then((res) => {
      const v = mapVersion(res.data && res.data.version);
      return v
        ? [{ versionNo: v.versionNo, advisor: v.advisor, reviewedAt: v.reviewedAt, isCurrent: v.isCurrent }]
        : [];
    })
    .catch(() => []);

// Parent's children (for the report selector). Reuses the existing guardian endpoint.
export const getChildren = () =>
  instance({ method: "get", url: "/guardian/students" })
    .then((res) => res.data || [])
    .catch(() => []);
